import React from "react";
import { Banner, Button, FormField, Input, StatusPill } from "@/ds";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type { MailDomainRecord, MailDomainSending, MailDomainView } from "@/api";
import type { PushToast } from "@/notifications/toast-queue";
import { useLang, useT } from "../i18n";
import {
  MAIL_DOMAIN_DEFAULT_LOCAL_PART,
  mailDomainActions,
  mailDomainAddressPreview,
  mailDomainCheckToast,
  mailDomainClaimRefusal,
  mailDomainCurrentStep,
  mailDomainDisconnectConfirmed,
  mailDomainElapsed,
  mailDomainFormErrors,
  mailDomainNext,
  mailDomainRecordStatus,
  mailDomainRelayWait,
  mailDomainRelayWaitKey,
  mailDomainSendingActions,
  mailDomainSendingTone,
  mailDomainStepRecords,
  mailDomainSteps,
  mailDomainTone,
  sortMailDomains,
  type MailDomainStep,
} from "./screen-mail-domains.logic";
import {
  mailDomainAiPrompt,
  type MailDomainPromptSource,
} from "./screen-mail-domains.prompt";
import styles from "./screen-mail-domains.module.css";

/**
 * A business's own mail domain (#1328, part of epic #1322; laid out as steps by #1424).
 *
 * The screen a business is sent to when it wants enquiries to arrive at its own address instead of
 * ours, and replies to leave from it. Each domain is a short progress — receive enquiries, reply
 * from your own address, done — under one "next" sentence, so somebody opening the page sees where
 * they stand and the single thing to do before reading anything else. The open step shows only its
 * own records; finished steps fold to a line; a domain that is fully set up folds to a summary.
 *
 * "We have not seen your record yet" and "we could not look at all" are still shown differently and
 * worded differently, because one is a wait and the other is ours to fix, and a business told the
 * wrong one either sits on the phone or re-edits a zone that was already correct. The relay's
 * confirmation gets the same care: a wait that is still normal says so, and says for how long.
 *
 * Nothing here decides anything. Which state a claim is in, whether a record is confirmed, and
 * whether a domain may be withdrawn are all server verdicts; this renders them.
 */

/** How long "Kopiert" stays on a copy button before it offers to copy again. */
const COPIED_FEEDBACK_MS = 2000;

/** How often "enabled 5 minutes ago" and the wait wording move on while somebody is waiting. */
const CLOCK_TICK_MS = 60_000;
/** How long typing pauses before a domain is asked about a previous address (#1446). */
const PREVIOUS_ADDRESS_DELAY_MS = 400;

/** The last look at DNS, kept only long enough to say what was asked for and what came back. */
type CheckNote = { id: string; recordName: string; detail: string | null };

function CopyValue({ value, testId }: { value: string; testId: string }) {
  const t = useT();
  const [copied, setCopied] = React.useState(false);
  // The value is selectable text either way, so a button that cannot work is absent rather
  // than silent.
  const canCopy = clipboardAvailable();

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={styles.valueCell}>
      <code className={styles.value}>{value}</code>
      {canCopy && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void copy();
          }}
          data-testid={testId}
        >
          {copied ? t("mailDomain.copied") : t("mailDomain.copy")}
        </Button>
      )}
    </div>
  );
}

/** A line-icon tick, drawn in the text colour so it follows the theme. */
function CheckMark() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M3.5 8.5l3 3 6-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The records to publish, one row each, exactly as the server gave them.
 *
 * With `sending`, the priority column gives way to the relay's verdict per record: only the two
 * sending records have a verdict, and neither of them has a priority.
 */
function RecordsTable({
  records,
  sending,
}: {
  records: readonly MailDomainRecord[];
  sending?: MailDomainSending;
}) {
  const t = useT();
  return (
    <div className={styles.recordsWrap}>
      <table
        className={sending ? `${styles.records} ${styles.withStatus}` : styles.records}
        data-testid="mail-domain-records"
      >
        <thead>
          <tr>
            <th scope="col">{t("mailDomain.col.type")}</th>
            <th scope="col">{t("mailDomain.col.name")}</th>
            {!sending && <th scope="col">{t("mailDomain.col.priority")}</th>}
            <th scope="col">{t("mailDomain.col.value")}</th>
            {sending && <th scope="col">{t("mailDomain.col.status")}</th>}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const status = sending ? mailDomainRecordStatus(record, sending) : null;
            return (
              <tr key={record.kind} data-testid="mail-domain-record" data-kind={record.kind}>
                <td data-label={t("mailDomain.col.type")}>{record.type}</td>
                <td data-label={t("mailDomain.col.name")}>
                  <CopyValue value={record.name} testId={`mail-domain-copy-name-${record.kind}`} />
                </td>
                {!sending && (
                  <td data-label={t("mailDomain.col.priority")} className="neo-num">
                    {record.priority === null ? <span className="subtle">—</span> : record.priority}
                  </td>
                )}
                <td data-label={t("mailDomain.col.value")}>
                  <CopyValue value={record.value} testId={`mail-domain-copy-value-${record.kind}`} />
                </td>
                {sending && (
                  <td data-label={t("mailDomain.col.status")}>
                    {status && (
                      <span data-testid="mail-domain-record-status" data-status={status}>
                        <StatusPill
                          tone={status === "confirmed" ? "success" : "progress"}
                          label={t(`mailDomain.record.${status}`)}
                        />
                      </span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * How to publish a step's records, for someone who has never opened a DNS panel (#1330).
 *
 * Deliberately not a walkthrough per registrar. Strato, IONOS, GoDaddy, Cloudflare and All-Inkl
 * each name and nest these settings differently and redesign them on their own schedule, so a
 * screenshot tour of each would be wrong within a year and wrong silently. This says the part that
 * is true everywhere and hands the registrar-specific half to the assistant beside it.
 *
 * It repeats no record value: the table is the single copy. Folded until asked for (#1424) — the
 * "next" line already says what to do, and this is the help for whoever needs more than that.
 */
function MailDomainHowTo({ domain, step }: { domain: string; step: "receive" | "send" }) {
  const t = useT();
  const sending = step === "send";
  return (
    <details className={styles.more} data-testid="mail-domain-howto">
      <summary className={styles.moreSummary}>{t("mailDomain.howTo.title")}</summary>
      <div className={styles.moreBody}>
        <ol className={styles.howToSteps}>
          <li>{t("mailDomain.howTo.step1")}</li>
          <li>{t(sending ? "mailDomain.howTo.send.step2" : "mailDomain.howTo.step2")}</li>
          <li>{t(sending ? "mailDomain.howTo.send.step3" : "mailDomain.howTo.step3")}</li>
        </ol>
        <p className={styles.meta}>
          {t(sending ? "mailDomain.howTo.send.safe" : "mailDomain.howTo.safe", { domain })}
        </p>
        {!sending && (
          <>
            <h4 className={styles.howToHeading}>{t("mailDomain.howTo.waitingTitle")}</h4>
            <p className={styles.meta}>{t("mailDomain.howTo.waiting")}</p>
          </>
        )}
      </div>
    </details>
  );
}

function clipboardAvailable(): boolean {
  // Not every context has a clipboard: an insecure origin, an old browser, a webview.
  return typeof navigator !== "undefined" && Boolean(navigator.clipboard?.writeText);
}

/**
 * The prompt for whatever assistant the business already has open (#1330).
 *
 * Built here from the same records the step's table renders, never from a second copy that could
 * drift from them. Nothing else about the claim is in scope: `mailDomainAiPrompt` takes a domain
 * and its records, so the id, the address and the check history are unreachable from it.
 *
 * A button that silently does nothing is worse than no button, so there are three outcomes rather
 * than two. Copied says so on the button. A browser with no clipboard never shows a button at all
 * and shows the text instead. A copy that was offered and then failed says that in words and
 * reveals the same text, selected, so the keyboard still works.
 *
 * The text can also just be read, at any time. We are asking a business to paste something into a
 * third party's assistant on our say-so that it contains nothing but their public DNS records;
 * being able to look is what makes that a claim they can check rather than one they have to take.
 */
function MailDomainAiPrompt({ source }: { source: MailDomainPromptSource }) {
  const t = useT();
  const [copied, setCopied] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  // Shown from the start where there is no button to press; otherwise the reader's to open.
  const [revealed, setRevealed] = React.useState(() => !clipboardAvailable());
  const textRef = React.useRef<HTMLTextAreaElement>(null);
  const textId = React.useId();
  const canCopy = clipboardAvailable();
  const prompt = React.useMemo(() => mailDomainAiPrompt(source, t), [source, t]);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setFailed(false);
      setCopied(true);
    } catch {
      setCopied(false);
      setFailed(true);
      setRevealed(true);
      // Selected on the next render, once the textarea exists to select.
      setTimeout(() => textRef.current?.select(), 0);
    }
  };

  return (
    <details className={styles.more} data-testid="mail-domain-prompt">
      <summary className={styles.moreSummary}>{t("mailDomain.prompt.title")}</summary>
      <div className={styles.moreBody}>
        <p className={styles.meta}>{t("mailDomain.prompt.hint")}</p>
        {canCopy && (
          <div className={styles.promptActions}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void copy();
              }}
              data-testid="mail-domain-prompt-copy"
            >
              {copied ? t("mailDomain.prompt.copied") : t("mailDomain.prompt.copy")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={revealed}
              aria-controls={textId}
              onClick={() => setRevealed((shown) => !shown)}
              data-testid="mail-domain-prompt-toggle"
            >
              {revealed ? t("mailDomain.prompt.hide") : t("mailDomain.prompt.show")}
            </Button>
          </div>
        )}
        {failed && (
          <p className={styles.meta} data-testid="mail-domain-prompt-copy-failed">
            {t("mailDomain.prompt.copyFailed")}
          </p>
        )}
        {revealed && (
          <textarea
            ref={textRef}
            id={textId}
            className={styles.promptText}
            readOnly
            rows={12}
            value={prompt}
            aria-label={t("mailDomain.prompt.label")}
            data-testid="mail-domain-prompt-text"
          />
        )}
      </div>
    </details>
  );
}

export interface MailDomainsScreenProps {
  pushToast: PushToast;
  /**
   * #1432: the claims as the screen now holds them, after every read that succeeded — for the card
   * above this panel in Settings → Email. Never called for a failed read, which is not "no domain".
   */
  onChange?: (rows: readonly MailDomainView[]) => void;
}

export function MailDomainsScreen({ pushToast, onChange }: MailDomainsScreenProps) {
  const t = useT();
  const { lang } = useLang();
  // #1536: the domains from the last visit stay on screen while they refresh.
  const [rows, setRows] = React.useState<MailDomainView[] | null>(() => {
    const remembered = cachedRead("listMailDomains");
    return remembered ? sortMailDomains(remembered) : null;
  });
  // Only this visit's answers are reported: Settings → Email must still see a failed refresh.
  const rememberedRows = React.useRef(rows).current;
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [domain, setDomain] = React.useState("");
  // #1446: the name before the @, prefilled with what a business gets without choosing.
  const [localPart, setLocalPart] = React.useState(MAIL_DOMAIN_DEFAULT_LOCAL_PART);
  // #1446: the address the server said a domain comes back with, and the spelling it answered for.
  const [previous, setPrevious] = React.useState<{ domain: string; address: string | null } | null>(
    null,
  );
  const [formError, setFormError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<string | null>(null);
  const [note, setNote] = React.useState<CheckNote | null>(null);
  // Folded records and details, by `<claim id>:<part>`. The reader's, never reset by a reload.
  const [open, setOpen] = React.useState<ReadonlySet<string>>(() => new Set());
  const [now, setNow] = React.useState(() => Date.now());

  const locale = lang === "de" ? "de-DE" : "en-GB";
  const formatWhen = React.useCallback(
    (iso: string) => new Date(iso).toLocaleString(locale),
    [locale],
  );
  const relative = React.useMemo(
    () => new Intl.RelativeTimeFormat(locale, { numeric: "always" }),
    [locale],
  );
  const ago = (iso: string) => {
    const elapsed = mailDomainElapsed(iso, now);
    return elapsed ? relative.format(-elapsed.value, elapsed.unit) : t("mailDomain.justNow");
  };

  const load = React.useCallback(() => {
    setError(null);
    cockpit.listMailDomains().then(
      (list) => {
        setRows(sortMailDomains(list));
        setNow(Date.now());
      },
      (e: any) => {
        // Fail closed in the UI too: a server that did not answer is an error, never "you have
        // no domains" — which would invite a business to claim one it already holds. The error is
        // set before the empty list because these renders are not batched: the other order leaves
        // one render holding an empty list with no error beside it, and #1432's card above this
        // panel would read that render as "no domain".
        setError(e?.message || t("common.error"));
        setRows([]);
      },
    );
  }, [t]);

  React.useEffect(() => {
    load();
  }, [load]);

  // A failed read leaves `rows` empty with an error beside it, and that is not "no domain".
  // Read through a ref so a parent passing a fresh callback each render cannot re-run this.
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  React.useEffect(() => {
    if (rows !== null && rows !== rememberedRows && error === null) onChangeRef.current?.(rows);
  }, [rows, rememberedRows, error]);

  // The clock only runs while somebody is waiting on the relay: that is the one place the screen
  // says "5 minutes ago", and the one place the wording changes as time passes.
  const waiting = rows?.some((row) => mailDomainActions(row).check === "sending") ?? false;
  React.useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [waiting]);

  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const fail = (e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message });

  // #1446: a domain this business connected before comes back with the address it had, so once the
  // server has answered for the spelling on screen, the form shows that address instead of a name
  // to choose. Asked when typing pauses; an answer for an earlier spelling is never applied. Until
  // one arrives the field stays, and the claim itself refuses a different name either way.
  const typedDomain = domain.trim();
  React.useEffect(() => {
    if (!typedDomain) return;
    let current = true;
    const timer = setTimeout(() => {
      cockpit.previousMailDomainAddress(typedDomain).then(
        (result) => {
          if (current) setPrevious({ domain: typedDomain, address: result.emailAddress });
        },
        // Not knowing is not "no previous address": keep the field and let the claim decide.
        () => undefined,
      );
    }, PREVIOUS_ADDRESS_DELAY_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [typedDomain]);
  const restored = previous && previous.domain === typedDomain ? previous.address : null;
  const preview = mailDomainAddressPreview(localPart, domain);

  const claim = () => {
    const errors = mailDomainFormErrors(domain, restored ? null : localPart);
    if (errors.length) {
      setFormError(errors.map((key) => t(key)).join(" · "));
      return;
    }
    setBusy(true);
    setFormError(null);
    cockpit
      // No name for a restored address: the server gives it back and would refuse any other.
      .claimMailDomain(domain, restored ? undefined : localPart.trim())
      .then((claimed) => {
        setDomain("");
        setLocalPart(MAIL_DOMAIN_DEFAULT_LOCAL_PART);
        setPrevious(null);
        setNote(null);
        pushToast({ tone: "success", title: t("mailDomain.claimed", { domain: claimed.domain }) });
        load();
      })
      // Read next to the box that produced it, not in a toast that has gone by the time the
      // person looks back. Translated where the server named a refusal we have words for (#1358),
      // otherwise the server's own sentence.
      .catch((e: any) => {
        const refusal = mailDomainClaimRefusal(e);
        setFormError(refusal ? t(refusal.key, refusal.vars) : e?.message || t("common.error"));
        // Pressed before the lookup answered: the refusal names the address, so the form stops
        // offering a choice now and the next press restores it.
        if (refusal?.key === "mailDomain.error.addressFixed" && refusal.vars.address) {
          setPrevious({ domain: typedDomain, address: refusal.vars.address });
        }
      })
      .finally(() => setBusy(false));
  };

  const check = (row: MailDomainView) => {
    setBusy(true);
    setNote(null);
    cockpit
      .checkMailDomain(row.id)
      .then((result) => {
        // Only a check that asked DNS has a record to name; a sending check asked the relay.
        if (result.recordName) {
          setNote({ id: row.id, recordName: result.recordName, detail: result.detail });
        }
        const toast = mailDomainCheckToast(row, result.domain);
        pushToast({
          tone: toast.tone,
          title: t(toast.key, { domain: result.domain.domain }),
          msg: toast.withDetail ? result.detail ?? undefined : undefined,
        });
      })
      .catch(fail)
      // Reloaded either way, deliberately. A check that proved the domain and then failed to
      // reach the mail server has still moved the claim forward; leaving the old state on screen
      // would tell the business to publish a record it has already published.
      .finally(() => {
        setBusy(false);
        load();
      });
  };

  // Sending as the business's own address (#1329). Deliberately the same shape as `check` and
  // `withdraw`: reload either way, because a call that failed part-way has still changed the claim
  // and the screen must show where it actually stopped rather than where it hoped to arrive.
  const setSending = (row: MailDomainView, on: boolean) => {
    setBusy(true);
    setNote(null);
    const call = on
      ? cockpit.enableMailDomainSending(row.id)
      : cockpit.disableMailDomainSending(row.id);
    call
      .then(() => {
        pushToast({
          tone: "success",
          title: t(on ? "mailDomain.sending.enabled" : "mailDomain.sending.disabled", {
            domain: row.domain,
          }),
        });
      })
      .catch(fail)
      .finally(() => {
        setBusy(false);
        load();
      });
  };

  const withdraw = (row: MailDomainView) => {
    setBusy(true);
    cockpit
      .withdrawMailDomain(row.id)
      .then(() => {
        setPending(null);
        setNote(null);
        pushToast({ tone: "success", title: t("mailDomain.withdrawn", { domain: row.domain }) });
      })
      .catch(fail)
      // Same reason as the check: a withdrawal that failed part-way has still changed the claim,
      // and the screen has to show where it actually stopped.
      .finally(() => {
        setBusy(false);
        load();
      });
  };

  // Disconnecting a receiving domain (#1429): one open panel at a time, and the name typed into it.
  const [disconnecting, setDisconnecting] = React.useState<string | null>(null);
  const [typed, setTyped] = React.useState("");
  const [mismatch, setMismatch] = React.useState(false);

  const openDisconnect = (id: string | null) => {
    setDisconnecting(id);
    setTyped("");
    setMismatch(false);
  };

  const disconnect = (row: MailDomainView) => {
    // The server compares the name again; this only spares a round trip for an obvious slip.
    if (!mailDomainDisconnectConfirmed(typed, row.domain)) {
      setMismatch(true);
      return;
    }
    setBusy(true);
    cockpit
      .disconnectMailDomain(row.id, typed.trim())
      .then(() => {
        openDisconnect(null);
        setNote(null);
        pushToast({ tone: "success", title: t("mailDomain.disconnected", { domain: row.domain }) });
      })
      .catch(fail)
      // Reloaded either way, like a withdrawal: a disconnect that stopped part-way leaves a claim
      // that no longer receives, and the screen has to offer the withdrawal that finishes it.
      .finally(() => {
        setBusy(false);
        load();
      });
  };

  /** What disconnecting does, what to change in DNS and in which order, then the typed name. */
  const renderDisconnect = (row: MailDomainView) => {
    const mx = row.records.filter((record) => record.kind === "mx");
    const rest = row.records.filter((record) => record.kind !== "mx");
    const base = `mail-domain-${row.id}-disconnect`;
    return (
      <div
        className={styles.disconnect}
        role="group"
        aria-labelledby={`${base}-title`}
        data-testid="mail-domain-disconnect-panel"
      >
        <h3 id={`${base}-title`} className={styles.disconnectTitle}>
          {t("mailDomain.disconnect.title", { domain: row.domain })}
        </h3>
        <p className={styles.hint}>
          {t("mailDomain.disconnect.effect", { address: row.emailAddress })}
        </p>
        <h4 className={styles.recordsTitle}>{t("mailDomain.disconnect.stepsTitle")}</h4>
        <ol className={styles.disconnectSteps}>
          <li>{t("mailDomain.disconnect.step.decide")}</li>
          <li>
            {t("mailDomain.disconnect.step.mx")}
            <div className={styles.disconnectRecords}>
              <RecordsTable records={mx} />
            </div>
          </li>
          {rest.length > 0 && (
            <li>
              {t("mailDomain.disconnect.step.rest")}
              <div className={styles.disconnectRecords}>
                <RecordsTable records={rest} />
              </div>
            </li>
          )}
          <li>{t("mailDomain.disconnect.step.website")}</li>
        </ol>
        <div className={styles.disconnectConfirm}>
          <FormField label={t("mailDomain.disconnect.confirmLabel", { domain: row.domain })}>
            <Input
              value={typed}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTyped(e.target.value);
                setMismatch(false);
              }}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={mismatch || undefined}
              aria-describedby={mismatch ? `${base}-error` : undefined}
              data-testid="mail-domain-disconnect-input"
            />
          </FormField>
          {mismatch && (
            <p
              id={`${base}-error`}
              className={styles.fieldError}
              role="alert"
              data-testid="mail-domain-disconnect-error"
            >
              {t("mailDomain.disconnect.mismatch")}
            </p>
          )}
          <div className={styles.confirmActions}>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => openDisconnect(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={() => disconnect(row)}
              data-testid="mail-domain-disconnect-now"
            >
              {t("mailDomain.disconnect.now")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /** The quiet danger action that opens the disconnect panel; never the card's primary button. */
  const disconnectButton = (row: MailDomainView) =>
    mailDomainActions(row).disconnect &&
    disconnecting !== row.id && (
      <Button
        variant="danger-quiet"
        size="sm"
        disabled={busy}
        onClick={() => openDisconnect(row.id)}
        data-testid="mail-domain-disconnect"
      >
        {t("mailDomain.disconnect")}
      </Button>
    );

  /** A "show records" switch for records that are already published and only there to look up. */
  const recordsToggle = (key: string, controls: string) => {
    const shown = open.has(key);
    return (
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={shown}
        aria-controls={controls}
        onClick={() => toggle(key)}
        data-testid="mail-domain-records-toggle"
      >
        {shown ? t("mailDomain.records.hide") : t("mailDomain.records.show")}
      </Button>
    );
  };

  const renderReceiveStep = (row: MailDomainView) => {
    const records = mailDomainStepRecords(row.records, "receive");
    const finishing = row.state === "finishing";
    const key = `${row.id}:receive`;
    const recordsId = `mail-domain-${row.id}-receive-records`;
    return (
      <div className={styles.stepContent}>
        <p className={styles.hint}>{t(`mailDomain.hint.${row.state}`)}</p>
        {row.state === "checkUnavailable" && (
          <Banner tone="warning" title={t("mailDomain.alert.checkUnavailable.title")}>
            {t("mailDomain.alert.checkUnavailable.body")}
          </Banner>
        )}
        {finishing ? (
          // Published and proven: nothing left to add, so the records are there to look up only.
          <>
            <div>{recordsToggle(key, recordsId)}</div>
            {open.has(key) && (
              <div id={recordsId}>
                <RecordsTable records={records} />
              </div>
            )}
          </>
        ) : (
          <>
            <h4 className={styles.recordsTitle}>{t("mailDomain.records.title")}</h4>
            <RecordsTable records={records} />
          </>
        )}
        <div className={styles.stepActions}>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => check(row)}
            data-testid="mail-domain-check"
          >
            {t("mailDomain.check")}
          </Button>
          <p className={styles.meta} data-testid="mail-domain-last-checked">
            {row.lastCheckedAt && row.lastCheckOutcome
              ? `${t("mailDomain.lastChecked", { when: formatWhen(row.lastCheckedAt) })} · ${t(
                  `mailDomain.outcome.${row.lastCheckOutcome}`,
                )}`
              : t("mailDomain.neverChecked")}
          </p>
        </div>
        {note && note.id === row.id && (
          <p className={styles.meta} data-testid="mail-domain-check-note">
            {t("mailDomain.lookedFor", { record: note.recordName })}
            {note.detail ? ` · ${note.detail}` : ""}
          </p>
        )}
        {!finishing && (
          <div className={styles.moreGroup}>
            <MailDomainHowTo domain={row.domain} step="receive" />
            <MailDomainAiPrompt source={{ domain: row.domain, records }} />
          </div>
        )}
      </div>
    );
  };

  const renderSendStep = (row: MailDomainView) => {
    const sendingActions = mailDomainSendingActions(row.state, row.sending.state);
    if (row.sending.state === "notRequested") {
      return (
        <div className={styles.stepContent} data-testid="mail-domain-sending" data-state="notRequested">
          <p className={styles.hint}>
            {t("mailDomain.sending.hint.notRequested", { address: row.emailAddress })}
          </p>
          {sendingActions.enable && (
            <div className={styles.stepActions}>
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => setSending(row, true)}
                data-testid="mail-domain-sending-enable"
              >
                {t("mailDomain.sending.enable")}
              </Button>
            </div>
          )}
        </div>
      );
    }
    const records = mailDomainStepRecords(row.records, "send");
    const wait = mailDomainRelayWait(row.sending.requestedAt, now);
    const waitKey = mailDomainRelayWaitKey(row.sending.state, wait);
    const { requestedAt, checkedAt } = row.sending;
    const since = requestedAt
      ? checkedAt
        ? t("mailDomain.sending.since", { when: ago(requestedAt), checked: ago(checkedAt) })
        : t("mailDomain.sending.sinceUnchecked", { when: ago(requestedAt) })
      : null;
    return (
      <div className={styles.stepContent} data-testid="mail-domain-sending" data-state={row.sending.state}>
        {row.sending.state === "stale" ? (
          <Banner tone="warning" title={t("mailDomain.sending.state.stale")}>
            {t("mailDomain.sending.hint.stale")}
          </Banner>
        ) : (
          waitKey && (
            <p className={styles.wait} data-testid="mail-domain-relay-wait" data-wait={wait}>
              {t(waitKey)}
            </p>
          )
        )}
        <h4 className={styles.recordsTitle}>{t("mailDomain.records.title")}</h4>
        <RecordsTable records={records} sending={row.sending} />
        <div className={styles.stepActions}>
          {mailDomainActions(row).check === "sending" && (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => check(row)}
              data-testid="mail-domain-check"
            >
              {t("mailDomain.checkSending")}
            </Button>
          )}
          {sendingActions.disable && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => setSending(row, false)}
              data-testid="mail-domain-sending-disable"
            >
              {t("mailDomain.sending.disable")}
            </Button>
          )}
        </div>
        {since && (
          <p className={styles.meta} data-testid="mail-domain-sending-since">
            {since}
          </p>
        )}
        <div className={styles.moreGroup}>
          <MailDomainHowTo domain={row.domain} step="send" />
          <MailDomainAiPrompt source={{ domain: row.domain, records }} />
        </div>
      </div>
    );
  };

  const renderStep = (row: MailDomainView, step: MailDomainStep, index: number) => {
    const current = step.status === "current";
    const receiveKey = `${row.id}:receive`;
    const receiveRecordsId = `mail-domain-${row.id}-receive-records`;
    const sendRequested = row.sending.state !== "notRequested";
    return (
      <li
        key={step.key}
        className={styles.step}
        data-testid="mail-domain-step"
        data-step={step.key}
        data-status={step.status}
        aria-current={current ? "step" : undefined}
      >
        <span className={styles.marker} data-status={step.status} aria-hidden="true">
          {step.status === "done" ? <CheckMark /> : index + 1}
        </span>
        <div className={styles.stepBody}>
          <div className={styles.stepHead}>
            <h3 className={styles.stepTitle}>
              {t(`mailDomain.step.${step.key}`)}
              <span className="sr-only"> ({t(`mailDomain.stepStatus.${step.status}`)})</span>
            </h3>
            {step.key === "receive" && step.status === "done" && (
              <>
                <span className={styles.stepMeta}>{row.emailAddress}</span>
                <span className={styles.stepToggle}>
                  {recordsToggle(receiveKey, receiveRecordsId)}
                </span>
              </>
            )}
            {step.key === "send" && (!current || !sendRequested) && (
              <span className={styles.stepMeta}>{t("mailDomain.step.optional")}</span>
            )}
            {step.key === "send" && current && sendRequested && (
              <StatusPill
                tone={mailDomainSendingTone(row.sending.state)}
                label={t(`mailDomain.sending.state.${row.sending.state}`)}
              />
            )}
          </div>
          {step.key === "receive" && step.status === "done" && open.has(receiveKey) && (
            <div id={receiveRecordsId}>
              <RecordsTable records={mailDomainStepRecords(row.records, "receive")} />
            </div>
          )}
          {step.key === "receive" && current && renderReceiveStep(row)}
          {step.key === "send" && current && renderSendStep(row)}
        </div>
      </li>
    );
  };

  const renderProgress = (row: MailDomainView) => {
    const actions = mailDomainActions(row);
    const next = mailDomainNext(row);
    const confirming = pending === row.id;
    return (
      <section
        key={row.id}
        className={styles.card}
        data-testid="mail-domain-card"
        data-state={row.state}
        data-step={mailDomainCurrentStep(row)}
        aria-labelledby={`mail-domain-${row.id}`}
      >
        <div className={styles.cardHead}>
          <div className={styles.identity}>
            <h2 id={`mail-domain-${row.id}`} className={styles.domain}>
              {row.domain}
            </h2>
            <StatusPill tone={mailDomainTone(row.state)} label={t(`mailDomain.state.${row.state}`)} />
          </div>
          {actions.withdraw && !confirming && (
            <Button
              variant="danger-quiet"
              size="sm"
              disabled={busy}
              onClick={() => setPending(row.id)}
              data-testid="mail-domain-withdraw"
            >
              {t("mailDomain.withdraw")}
            </Button>
          )}
          {disconnectButton(row)}
        </div>

        {disconnecting === row.id && renderDisconnect(row)}

        {confirming && (
          <div className={styles.confirm} data-testid="mail-domain-withdraw-confirm">
            <span>{t("mailDomain.withdrawConfirm", { domain: row.domain })}</span>
            <div className={styles.confirmActions}>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setPending(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() => withdraw(row)}
                data-testid="mail-domain-withdraw-now"
              >
                {t("mailDomain.withdrawNow")}
              </Button>
            </div>
          </div>
        )}

        {next && (
          <p className={styles.next} data-testid="mail-domain-next" data-next={next}>
            <span className={styles.nextLabel}>{t("mailDomain.next")}</span>{" "}
            {t(`mailDomain.next.${next}`, { address: row.emailAddress })}
          </p>
        )}

        <ol className={styles.steps}>
          {mailDomainSteps(row).map((step, index) => renderStep(row, step, index))}
        </ol>
      </section>
    );
  };

  /** A domain with nothing left to do: one line, and everything else behind Details. */
  const renderReady = (row: MailDomainView) => {
    const key = `${row.id}:details`;
    const detailsId = `mail-domain-${row.id}-details`;
    const shown = open.has(key);
    const sendingActions = mailDomainSendingActions(row.state, row.sending.state);
    return (
      <section
        key={row.id}
        className={styles.card}
        data-testid="mail-domain-card"
        data-state={row.state}
        data-step="done"
        aria-labelledby={`mail-domain-${row.id}`}
      >
        <div className={styles.cardHead}>
          <div className={styles.identity}>
            <h2 id={`mail-domain-${row.id}`} className={styles.domain}>
              {row.domain}
            </h2>
            <StatusPill tone="success" label={t("mailDomain.ready")} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={shown}
            aria-controls={detailsId}
            onClick={() => toggle(key)}
            data-testid="mail-domain-details-toggle"
          >
            {shown ? t("mailDomain.details.hide") : t("mailDomain.details.show")}
          </Button>
        </div>
        <p className={styles.summary} data-testid="mail-domain-ready">
          <span className={styles.summaryMark} aria-hidden="true">
            <CheckMark />
          </span>
          <span>
            {t(
              row.sending.state === "sending"
                ? "mailDomain.ready.summary"
                : "mailDomain.ready.summaryReceiveOnly",
              { address: row.emailAddress },
            )}
          </span>
        </p>
        {shown && (
          <div id={detailsId} className={styles.details} data-testid="mail-domain-details">
            <RecordsTable records={row.records} />
            <div className={styles.stepActions}>
              {sendingActions.disable && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => setSending(row, false)}
                  data-testid="mail-domain-sending-disable"
                >
                  {t("mailDomain.sending.disable")}
                </Button>
              )}
              {disconnectButton(row)}
            </div>
            {disconnecting === row.id && renderDisconnect(row)}
          </div>
        )}
      </section>
    );
  };

  const renderCard = (row: MailDomainView) =>
    mailDomainCurrentStep(row) === "done" ? renderReady(row) : renderProgress(row);

  // A business has one mail domain (#1430). With none, the form is the page rather than something
  // to find behind a button; with one, there is nothing to add, and the page says which to remove
  // first. The server refuses a second claim either way — this only stops offering one.
  const showForm = rows !== null && rows.length === 0 && !error;
  const held = rows && rows.length > 0 ? rows[0]! : null;
  // The move the held domain's own card offers: withdraw until it receives, disconnect after.
  const limitMove = held && mailDomainActions(held).disconnect
    ? "mailDomain.limit.disconnect"
    : "mailDomain.limit.withdraw";

  return (
    // The own-domain panel of Settings → Email (#1432), under Settings' heading rather than its own.
    <div data-testid="mail-domains">
      <h3 className="settings-feature-title">{t("mailDomain.title")}</h3>
      <p className="management-copy">{t("mailDomain.sub")}</p>
      {held && (
        <p className="management-copy" data-testid="mail-domain-limit" data-move={limitMove}>
          {t(limitMove, { domain: held.domain })}
        </p>
      )}

      {showForm && (
        <section
          className={styles.form}
          aria-labelledby="mail-domain-claim-title"
          data-testid="mail-domain-claim"
        >
          <h2 id="mail-domain-claim-title" className={styles.sectionTitle}>
            {t("mailDomain.claimTitle")}
          </h2>
          <FormField label={t("mailDomain.domain")} hint={t("mailDomain.domainHint")}>
            <Input
              value={domain}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDomain(e.target.value)}
              placeholder="anfragen.ihre-firma.de"
              data-testid="mail-domain-input"
            />
          </FormField>
          {restored ? (
            <p className="management-copy" data-testid="mail-domain-address-restored">
              {t("mailDomain.addressRestored", { address: restored })}
            </p>
          ) : (
            <>
              <FormField label={t("mailDomain.localPart")} hint={t("mailDomain.localPartHint")}>
                <Input
                  value={localPart}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalPart(e.target.value)}
                  placeholder={MAIL_DOMAIN_DEFAULT_LOCAL_PART}
                  data-testid="mail-domain-local-part"
                />
              </FormField>
              {preview && (
                <p className="management-copy" data-testid="mail-domain-address-preview">
                  {t("mailDomain.addressPreview", { address: preview })}
                </p>
              )}
            </>
          )}
          {formError && (
            <Banner tone="danger" title={t("common.error")}>
              {formError}
            </Banner>
          )}
          <div className={styles.formActions}>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={claim}
              data-testid="mail-domain-submit"
            >
              {t("mailDomain.claim")}
            </Button>
          </div>
        </section>
      )}

      {error && (
        <Banner tone="danger" title={t("common.error")}>
          {error}
        </Banner>
      )}
      {rows === null && !error && <p className="subtle" aria-busy="true">{t("common.loading")}</p>}
      {rows && rows.length === 0 && !error && <p className="subtle">{t("mailDomain.none")}</p>}

      {rows && rows.length > 0 && <div className={styles.list}>{rows.map(renderCard)}</div>}
    </div>
  );
}
